// Searchable single-select for long lists (role types, countries, timezones).
// Short lists (< ~10) should stay on `Select`. The value is a string id, or
// null while nothing is chosen: the input is then empty and shows the
// placeholder. There is deliberately no "Not specified" item — clearing the
// text would select it again and refill the input.
import { useMemo } from 'react';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from './components/combobox.tsx';

export interface SearchableOption {
  value: string;
  label: string;
  /** Options sharing a `group` render under one heading, in first-seen order. */
  group?: string;
}

interface OptionGroup {
  value: string;
  items: SearchableOption[];
}

interface Props {
  options: readonly SearchableOption[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Optional fields: an × in the input empties the value (onChange(null)). */
  clearable?: boolean;
  size?: 'sm' | 'default' | 'lg';
  id?: string;
  emptyText?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
}

const isGroup = (i: SearchableOption | OptionGroup): i is OptionGroup => 'items' in i;

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  clearable = false,
  size = 'default',
  id,
  emptyText = 'No matches.',
  ...aria
}: Props) {
  const items = useMemo<(SearchableOption | OptionGroup)[]>(() => {
    if (!options.some((o) => o.group)) return [...options];
    const out: (SearchableOption | OptionGroup)[] = [];
    const groups = new Map<string, OptionGroup>();
    for (const o of options) {
      if (!o.group) {
        out.push(o);
        continue;
      }
      let g = groups.get(o.group);
      if (!g) {
        g = { value: o.group, items: [] };
        groups.set(o.group, g);
        out.push(g);
      }
      g.items.push(o);
    }
    return out;
  }, [options]);
  const selected = options.find((o) => o.value === value) ?? null;

  return (
    <Combobox
      items={items}
      value={selected}
      onValueChange={(o: SearchableOption | null) => onChange(o ? o.value : null)}
      isItemEqualToValue={(a: SearchableOption, b: SearchableOption) => a.value === b.value}
    >
      <ComboboxInput
        id={id}
        size={size}
        placeholder={placeholder}
        showClear={clearable}
        {...aria}
      />
      <ComboboxContent>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: SearchableOption | OptionGroup) =>
            isGroup(item) ? (
              <ComboboxGroup key={item.value} items={item.items}>
                <ComboboxLabel>{item.value}</ComboboxLabel>
                {item.items.map((o) => (
                  <ComboboxItem key={o.value} value={o}>
                    {o.label}
                  </ComboboxItem>
                ))}
              </ComboboxGroup>
            ) : (
              <ComboboxItem key={item.value} value={item}>
                {item.label}
              </ComboboxItem>
            )
          }
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
